import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, startWith, tap, map, switchMap } from 'rxjs/operators';
import { Location } from "@angular/common";
import { dummyStory, readStory, Story, Comment, readComment } from '../../../../shared/interfaces/story';
import { FilterMarkUpPipe } from '../../../../shared/pipes/filter-mark-up.pipe';
import { FirestoreService } from '../../../../shared/services/firestore.service';
import { SeoService } from '../../../../core/services/seo.service';
import { nav_path } from '../../../../app-routing.module';
import { AuthService } from '../../../../core/services/auth.service';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConsoleLoggerService } from '../../../../core/services/console-logger.service';
import { DocumentReference } from '@angular/fire/firestore';
import { FirebaseError } from '@angular/fire/app/firebase';

@Component({
  selector: 'anon-story-page',
  templateUrl: './story-page.component.html',
  styleUrls: ['./story-page.component.scss']
})
export class StoryPageComponent implements OnDestroy {
  @ViewChild('commentInput') commentInput!: ElementRef;
  public readonly nav_path = nav_path;
  private readonly story_id?: string | null;
  public readonly storyPath? = 'stories/' + this.story_id;
  story$?: Observable<readStory>;
  public story: Story = dummyStory;
  public storyMarkup = '';
  public error: any;
  public commentSectionActive: boolean = false;
  public addCommentForm = new FormGroup({
    content: new FormControl('', Validators.required)
  });
  public storyComments$?: Observable<readComment[]>;
  public storyCommentsError: any;

  private isStoryOwner: boolean = false;

  private readonly unsubscribe: any;

  constructor(
    private route: ActivatedRoute,
    public db: FirestoreService,
    public location: Location,
    private filterMarkUp: FilterMarkUpPipe,
    private seo: SeoService,
    public auth: AuthService,
    private router: Router,
    private state: TransferState,
    private cLog: ConsoleLoggerService
  ) {
    this.story_id = this.route.snapshot.paramMap.get('storyID');
    this.storyPath = `stories/${this.story_id}`;

    const key = makeStateKey<readStory>('STORY');
    const existing = state.get(key, undefined);
    this.story$ = of(existing).pipe(
      switchMap(() => this.db.doc$(this.storyPath!) as Observable<readStory>),
      tap((story: readStory) => {
        if (!story) this.router.navigate([nav_path.pageNotFound]);
        this.seo.generateTags({
          title: story.title!,
          description: story.byline!,
          route: `${nav_path.stories}/${this.story_id}`,
          author: story.author?.name,
          type: 'article'
        });
        this.isStoryOwner = story.roles![auth.user?.uid!] === "owner";
        this.storyMarkup = this.filterMarkUp.transform(story.content!);
        this.storyComments$ = this.db.col$(`${this.storyPath}/comments`)
          .pipe(map((comments: any) =>
            comments.sort((a: readComment, b: readComment) => b.created?.seconds- a.created?.seconds) as readComment[]
          ), catchError(error => {
            this.storyCommentsError = error;
            return of([]);
          }));
        state.set(key, story);
      }),
      existing ? startWith(existing) : tap(),
      catchError(error => {
        this.error = error;
        return throwError(error);
      })
    );

    // this.unsubscribe = this.db.snapshot(
    //   this.storyPath!,
    //   async (snap) => {
    //     if (!snap.exists()) this.router.navigate([nav_path.pageNotFound]);
    //     this._story = snap.data() as readStory;
    //     this.seo.generateTags({
    //       title: this._story.title!,
    //       description: this._story.byline!,
    //       route: `${nav_path.stories}/${snap.id}`,
    //       author: (await this.db.docSnap(this._story.author!)).get('displayName'),
    //       type: 'article'
    //     });
    //     this.story$ = of(this._story);
    //     this.isStoryOwner = this._story.roles![auth.user?.uid!] === "owner";
    //     this.storyMarkup = this.filterMarkUp.transform(this._story.content);
    //     this.storyComments$ = this.db.col$(`${this.storyPath}/comments`)
    //       .pipe(map((comments: any) =>
    //             comments.sort((a: readComment, b: readComment) => b.created.seconds- a.created.seconds) as readComment[]
    //       ));
    //   },
    //   (error) => cLog.error(`error from test onSnapshot: `, error),
    //   () => cLog.log(`test onSnapshot completed!`));
  }
  get comment() { return this.addCommentForm.get('content'); }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.commentSectionActive) this.cancelComment();
    if (event.key === 'e' && !this.commentSectionActive) {
      if (this.isStoryOwner) this.router.navigate([nav_path.privateStories, this.story_id, 'edit']);
      // todo: allow writers to edit also
    }
  }

  typeComment() {
    if (!this.auth.user) {
      this.router.navigate([nav_path.signIn], {queryParams: {'redirectURL': this.location.path()}})
        .then(() => this.cLog.info(`must be signed in to add comments`));
    }
    this.commentSectionActive = true;
  }
  cancelComment() {
    this.commentSectionActive = false;
    this.commentInput.nativeElement.blur();
    this.addCommentForm.reset();
  }
  addComment() {
    const storyRef: DocumentReference = this.db.doc(this.storyPath!);
    const comment: Comment = {
      parent: storyRef,
      author: {
        name: this.auth.user!.displayName!,
        image: this.auth.user!.photoURL!
      },
      content: this.comment?.value!,
      user: this.db.doc(`users/${this.auth.user?.uid}`)
    }
    this.db.add(`${storyRef.path}/comments`, comment)
      .then((ref) => {
        this.cLog.log(`comment created: '${ref.id}'`);
        this.cancelComment();
      })
      .catch((error: FirebaseError) => this.cLog.error(`error adding comment: ${error.message}`));
  }

  ngOnDestroy() {
    // this.unsubscribe();
  }
}
